import { getApiError } from './getApiError';

// Status text the App Server's responder substitutes when a route's own explanation is
// discarded. Showing one of these is worse than showing nothing: it reads as the server's
// answer when the actual answer was thrown away.
const GENERIC_STATUS_TEXT = new Set(['Forbidden', 'Bad Request', 'Internal Server Error', 'Unauthorized', 'Not Found']);

/**
 * A sentence a user can act on, from any SuperFetch result.
 *
 * Builds on `getApiError`, which already knows where routes put the actionable reason
 * (`info` and `error` both beat the generic `message`). This adds the STATUS-aware layer that
 * a plain extractor cannot do:
 *
 *   · **401** — the common expired-token shape carries NO statusCode at any level.
 *     `unauthorised()` sends `{ error: <FirebaseAuthError>, message: 'Request is not
 *     authorised' }` and that error's toJSON is `{ code, message }`, so matching on the
 *     message (or the presence of `error.code`) is the only reliable signal. Without it the
 *     user is told "Request is not authorised" instead of to sign in again.
 *   · **5xx** — `error` holds `error.toString()`, a raw server exception. Suppressed BEFORE
 *     extraction, because `getApiError` would happily return it.
 *   · **generic status words** — rejected in favour of the caller's fallback.
 *
 * @param {object} res       the SuperFetch result
 * @param {string} fallback  shown when nothing usable was returned
 */
export const apiFailureMessage = (res, fallback = 'Something went wrong.') => {
	if (!res) return fallback;
	// Transport failure: SuperFetch returns { errors: 'Server Error', error: <Error> } and the
	// raw Error must never reach JSX.
	if (res.errors === 'Server Error') return 'Could not reach the server. Check your connection and try again.';

	const nested = typeof res.error === 'object' && res.error !== null && !Array.isArray(res.error) ? res.error : null;
	const status = res.statusCode ?? nested?.statusCode;

	if (status === 401 || res.message === 'Request is not authorised' || Boolean(nested?.code)) {
		return 'Your session has expired. Reload the page to sign in again.';
	}
	if (status >= 500) return 'The server could not complete this request. Try again shortly.';

	const detail = getApiError(res);
	return detail && !GENERIC_STATUS_TEXT.has(detail) ? detail : fallback;
};

export default apiFailureMessage;
